import React, {KeyboardEventHandler, ReactNode, useEffect, useMemo, useRef, useState} from 'react';
import {Input} from '~/components/Controls/Input';
import {ISelectItem} from '~/components/Controls/Select/common';
import {DropDown} from '~/components/DropDown';
import {IWithPopper} from '~/components/Popper';
import {QueryList} from '~/components/QueryList';
import {Intent} from '~/lib/intent';
import style from './index.module.scss';

export type ISelectProps<T> = IWithPopper &{
    items: ISelectItem<T>[];
    intent?: Intent;
    placeholder?: string;
    value: T;
    disabled?: boolean;
    onChange: (value: T) => void;
    searchable?: boolean;
    combobox?: boolean;
    autoFocus?: boolean;
    className?: string;
    rightIcon?: ReactNode;
} & (
    | {
        onChange: (value: T) => void;
        clearable?: false;
      }
    | {
        clearable: true;
        onChange: (value: T | null) => void;
      }
  );

export const SelectInput = <T extends any>({
    items,
    usePopper,
    popperOptions,
    dropUp,
    onChange,
    disabled,
    value,
    intent,
    clearable,
    searchable,
    combobox,
    autoFocus,
    placeholder,
    className,
    rightIcon
}: ISelectProps<T>) => {
    const [open, setOpen] = useState<boolean>(false);
    const [query, setQuery] = useState<string>('');
    const [activeIndex, setActiveIndex] = useState<number>(-1);
    const activeItemRef = useRef<HTMLDivElement>(null);
    const comboboxInputRef = useRef<HTMLInputElement>(null);

    const isCombobox = Boolean(combobox && searchable && !disabled);

    const selectedItem = (items || []).find(item => item.value === value);
    const toSeletItems = (items || []).filter(item => item.value !== value);

    const filteredItems = useMemo(
        () => toSeletItems.filter(item => item.label.toLocaleLowerCase().includes(query.toLocaleLowerCase())),
        [toSeletItems, query]
    );

    useEffect(() => {
        setActiveIndex(-1);
    }, [query, open]);

    useEffect(() => {
        activeItemRef.current?.scrollIntoView({block: 'nearest'});
    }, [activeIndex]);

    useEffect(() => {
        if (!open) {
            setQuery('');
        }
    }, [open]);

    useEffect(() => {
        if (isCombobox && open) {
            comboboxInputRef.current?.focus({preventScroll: true});
        }
    }, [open, isCombobox]);

    const selectItem = (item: ISelectItem<T>) => {
        setQuery('');
        setOpen(false);
        onChange(item.value);
    };

    const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = (event) => {
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            if (!open) {
                setOpen(true);
            }
            if (filteredItems.length) {
                setActiveIndex(index => Math.min(index + 1, filteredItems.length - 1));
            }
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setActiveIndex(index => Math.max(index - 1, 0));
        } else if (event.key === 'Enter') {
            event.preventDefault();
            if (activeIndex >= 0 && activeIndex < filteredItems.length) {
                selectItem(filteredItems[activeIndex]);
            }
        }
    };

    return <DropDown rightIcon={rightIcon} usePopper={usePopper} disabled={disabled} popperOptions={popperOptions} dropUp={dropUp}
        tabIndex={isCombobox ? -1 : undefined}
        content={() => <>
            {(searchable && !isCombobox && !disabled) && <div className={style.selectQuery}>
                <Input value={query} onChange={setQuery} onKeyDown={handleKeyDown}
                    rightIcon={<i className='fa fa-search'/>}
                    autoFocus/>
            </div>}
            <div className={style.selectItems}>
                <QueryList
                    items={filteredItems}
                    activeIndex={activeIndex}
                    onSelect={(item) => selectItem(item)}
                    emptyRenderer={<div className={style.selectEmpty}>Empty</div>}
                    itemRenderer={({onClick, item, index, isActive}) =>
                        <div ref={isActive ? activeItemRef : undefined} onClick={onClick}
                            className={[style.selectItem, isActive && style.selectItemActive].filter(Boolean).join(' ')}
                            key={index}>{item.label}</div>
                    }
                />
            </div>
        </>} isOpen={open} onToggle={setOpen}
        className={className} intent={intent}>
        {isCombobox ? (
            <div className={style.selectComboboxTrigger}>
                <input
                    ref={comboboxInputRef}
                    className={style.selectComboboxInput}
                    autoFocus={autoFocus}
                    value={open ? query : (selectedItem?.label ?? '')}
                    placeholder={selectedItem?.label || placeholder}
                    onChange={event => {
                        setQuery(event.target.value);
                        setOpen(true);
                    }}
                    onKeyDown={handleKeyDown}
                    onClick={event => {
                        event.stopPropagation();
                        setOpen(true);
                    }}
                />
                {(clearable && selectedItem && !disabled) &&
                <i className={['fa fa-close', style.selectClear].join(' ')} onClick={(event) => {
                    event.stopPropagation();
                    setQuery('');
                    onChange(null);
                }}/>}
            </div>
        ) : (
            <div className={style.selectCurrentItem}>
                {selectedItem ? <>
                    {selectedItem.label}
                    {(clearable && !disabled) &&
                    <i className={['fa fa-close', style.selectClear].join(' ')} onClick={(event) => {
                        event.stopPropagation();
                        onChange(null);
                    }}/>}
                </> : <span className={style.selectPlaceholder}>{placeholder}</span>}
            </div>
        )}
    </DropDown>;
};
